import { useState, useEffect } from "react";
import {
  getConditionsData,
  getPrevConditionsData,
  setConditionsData,
} from "../../../services/user.service";

import { useLocation } from "react-router-dom";

import { esES } from "@mui/x-data-grid/locales";
import { esES as pickersEsES } from "@mui/x-date-pickers/locales";
import { esES as coreEsES } from "@mui/material/locale";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { ThemeProvider, createTheme } from "@mui/material/styles";

import moment from "moment/moment";
import "moment/locale/es";
import { CondTable } from "./table";
import Button from "../../../components/button";
import styles from "./styles.module.css";
moment.locale("es");

const theme = createTheme(
  {
    palette: {
      mode: "light",
    },
    components: {
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: "none",
          },
          columnHeader: {
            backgroundColor: "hsl(var(--secondary))",
            color: "hsl(var(--muted-foreground))",
            fontWeight: 600,
          },
          columnHeaderTitle: {
            fontWeight: 600,
            color: "hsl(var(--muted-foreground))",
          },
        },
      },
    },
  },
  esES,
  pickersEsES,
  coreEsES
);

function MMConditions() {
  const [condData, setCondData] = useState({});
  const [prevData, setPrevData] = useState({});
  const [origCondData, setOrigCondData] = useState(new Map());
  const [updating, setUpdating] = useState("");
  const [selMonth, setSelmonth] = useState(moment().format("MMMM YYYY"));
  const [prevMonth, setPrevMonth] = useState("");

  const tablenames = {
    mmcompanies: "Nombres de los CM",
    mmconditions: "Condiciones de los CM",
    mmspreads: "Spreads durante SM",
    mmexonerationdays: "Días de exoneración",
    marketconditions: "Condiciones de mercado",
  };

  let location = useLocation();

  function compareObjectsWithoutId(obj1, obj2) {
    const obj1Keys = Object.keys(obj1).filter((key) => key !== "id");
    const obj2Keys = Object.keys(obj2).filter((key) => key !== "id");

    return (
      obj1Keys.every((key) => obj1[key] === obj2[key]) &&
      obj2Keys.every((key) => obj2[key] === obj1[key])
    );
  }

  function diffData(origData, data, monthstr) {
    const ymonth = moment(monthstr, "MMMM YYYY").format("yyyy-MM");
    const changes = {};
    const validatingerrors = [];

    Object.keys(data).forEach((tabName) => {
      const origArray = origData[tabName];
      const newArray = data[tabName];

      // Compare objects excluding 'id'
      const upsertEntries = newArray.filter((newItem) => {
        return !origArray.some((origItem) =>
          compareObjectsWithoutId(newItem, origItem)
        );
      });

      // Find deleted entries that are in origData but not in the newData
      const deletedEntries = origArray.filter((origItem) => {
        return !newArray.some((newItem) =>
          compareObjectsWithoutId(newItem, origItem)
        );
      });

      const wrongTimeEntries = upsertEntries.filter(
        (itm) => !itm.dtime.startsWith(ymonth)
      ).length;
      if (wrongTimeEntries > 0) {
        validatingerrors.push(
          `Para '${tablenames[tabName]}' hay ${wrongTimeEntries} fila${
            wrongTimeEntries > 1 ? "s" : ""
          } con el tiempo correspondiente a otro mes.`
        );
      }

      changes[tabName] = {
        upsertEntries, // Combine new and updated entries
        deletedEntries,
      };
    });

    return [changes, validatingerrors];
  }

  const refresh = () => {
    getCondData(moment(selMonth, "MMMM YYYY").format("yyyy-MM-DD"));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const getCondData = (month) => {
    setPrevMonth("");
    getConditionsData(month)
      .then((res) => {
        let conditionsData = JSON.parse(res.data);

        Object.entries(conditionsData).forEach(([k, v]) => {
          v.forEach((entry, idx) => {
            entry.id = idx;
          });
        });
        setCondData(conditionsData);
        setOrigCondData(structuredClone(conditionsData));
      })
      .catch((err) => console.log(err));

    getPrevConditionsData(month)
      .then((res) => {
        let conditionsData = JSON.parse(res.data);
        if (conditionsData.mmconditions[0]?.dtime) {
          setPrevMonth(
            moment(conditionsData.mmconditions[0]?.dtime).format("MMMM YYYY")
          );
          setPrevData(conditionsData);
        }
      })
      .catch((err) => console.log(err));
  };

  const handleMonthChange = (month) => {
    month.startOf("month");
    setSelmonth(month.format("MMMM YYYY"));
    getCondData(month.format("yyyy-MM-DD"));
  };

  const copyPrevData = () => {
    let nuData = { ...condData };
    const nudtime = moment(selMonth, "MMMM YYYY").format("yyyy-MM-DD");
    ["mmcompanies", "mmconditions", "mmspreads"].forEach((tab) => {
      nuData[tab] = prevData[tab].map((item, idx) => {
        let pritem = {
          ...item,
          dtime: nudtime, // Replace all dtime values with nudtime
          id: idx,
        };
        return pritem;
      });
    });
    setCondData(nuData);
  };

  const saveChanges = () => {
    let [stuffchanged, validatingerrors] = diffData(
      origCondData,
      condData,
      selMonth
    );

    if (validatingerrors.length > 0) {
      const errorstr = `Hemos encontrado errores.\n${validatingerrors.join(
        "\n"
      )}`;
      console.log(errorstr);
      setUpdating(errorstr);
    } else {
      let updateNeeded = Object.values(stuffchanged).some((changeSet) => {
        return (
          changeSet.upsertEntries.length > 0 ||
          changeSet.deletedEntries.length > 0
        );
      });

      if (updateNeeded) {
        setUpdating(
          `Actualizando condiciones de cumplimiento a las ${moment().format(
            "HH:mm:SS"
          )}.`
        );
        setConditionsData(stuffchanged)
          .then((response) => {
            setUpdating(
              `Respuesta recibida a las ${moment().format("HH:mm:SS")}:\n${
                response.data
              }`
            );
            refresh();
          })
          .catch((error) => {
            console.log(
              error,
              error?.response?.data ? error?.response?.data : ""
            );
            setUpdating(
              `Error recibido a las ${moment().format(
                "HH:mm:SS"
              )} mientras se actualizaba:\n${error}${
                error?.response?.data
                  ? `\nDetalle del error:${error?.response?.data}`
                  : ""
              }`
            );
            // refresh();
          });
      } else {
        setUpdating("Nada que actualizar");
      }
    }
  };

  return (
    <div id="MMCnd" className={styles.container}>
      <LocalizationProvider
        localeText={
          pickersEsES.components.MuiLocalizationProvider.defaultProps.localeText
        }
        dateAdapter={AdapterMoment}
        adapterLocale={"es"}
      >
        <div className={styles.header}>
          <div className={styles.title}>
            <h3>Mantenimiento de Creadores de Mercado</h3>
          </div>

          <div className={styles.buttonGroup}>
            <div id="picker">
              <DatePicker
                label="Mes de análisis"
                views={["year", "month"]}
                value={moment(selMonth, "MMMM YYYY")}
                onChange={handleMonthChange}
              />
            </div>
            <div className={styles.actionButtons}>
              <Button
                variant="outline"
                onClick={() => saveChanges()}
                style={{ borderColor: "#a5b4fc" }}
              >
                Guardar Cambios
              </Button>

              <Button
                variant="outline"
                title={
                  "Rellena las tablas con la información del último mes (anterior) para el que haya datos." +
                  `\n${
                    !prevMonth
                      ? "No hay datos para los meses anteriores."
                      : "Se usará " + prevMonth
                  }.`
                }
                onClick={() => copyPrevData()}
                disabled={!prevMonth}
                style={{ borderColor: "#93c5fd" }}
              >
                Heredar condiciones{" "}
                {!prevMonth ? "anteriores" : `de ${prevMonth}`}
              </Button>
            </div>
          </div>

          <pre
            className={`${styles.statusMessage} ${
              updating ? styles.statusMessageActive : ""
            }`}
          >
            {updating}
          </pre>
        </div>

        <div className={styles.tableContainer}>
          {Object.keys(condData).map((tabkey) => (
            <div key={tabkey} className={styles.tableWrapper}>
              <p className={styles.productSection}>
                {tablenames[tabkey]} - {selMonth}
              </p>
              <ThemeProvider theme={theme}>
                <CondTable
                  tablekey={tabkey}
                  setter={setCondData}
                  fulldata={condData}
                  month={selMonth}
                />
              </ThemeProvider>
            </div>
          ))}
        </div>
      </LocalizationProvider>
    </div>
  );
}

export default MMConditions;
